import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { ExportJob, JobStatus } from "types";
import { api } from "@/api/api";
import { xolaUri } from "@/api/xolaUri";
import { useResource } from "@/hooks/useResource";

const getFullUrl = (url: string): string => {
    return url.startsWith("http") ? url : `${xolaUri}${url}`;
};

export const useDisputeEvidence = (purchaseId: string) => {
    const [job, setJob] = useState<ExportJob>();
    const [refetchInterval, setRefetchInterval] = useState<number | undefined>();
    const [error, setError] = useState<string>();
    const [isProcessing, setIsProcessing] = useState(false);

    const { mutateAsync: createJob } = useMutation(async () => {
        const { data } = await api.get<ExportJob>(`/purchases/${purchaseId}/disputeEvidence`);
        return data;
    });

    const { data: jobData } = useResource<ExportJob>(["jobs", job?.id], null, {
        refetchInterval,
        enabled: !!job?.id,
    });

    useEffect(() => {
        if (!jobData) return;

        const terminalStatuses = [JobStatus.COMPLETED, JobStatus.ERROR, JobStatus.STALE];
        if (terminalStatuses.includes(jobData.status)) {
            setRefetchInterval(undefined);
            setJob(undefined);
            setIsProcessing(false);
            if (jobData.status === JobStatus.COMPLETED) {
                window.open(getFullUrl(jobData.url), "_blank", "noopener,noreferrer");
            } else {
                setError("Failed to generate evidence. Please try again.");
            }
        }
    }, [jobData]);

    const downloadEvidence = async () => {
        if (isProcessing) return;

        setIsProcessing(true);
        setError(undefined);
        try {
            const newJob = await createJob();
            setJob(newJob);
            setRefetchInterval(3000);
        } catch {
            setIsProcessing(false);
            setError("Failed to start evidence download. Please try again.");
        }
    };

    const isDownloading = isProcessing;

    return { isDownloading, downloadEvidence, error };
};
